import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { DidRepository } from '@wephone-core/model/repository/did';
import { DidEntity } from '@wephone-core/model/entity/did';
import { Subject, ReplaySubject, BehaviorSubject, Observable } from 'rxjs';
import { UserGroupEntity } from '@wephone-core/model/entity/usergroup';
import { UserGroupRepository } from '@wephone-core/model/repository/usergroup';
import {
  DialogService,
  IFlexTableConfig,
  parseDateTime,
  regexSearch,
  IFlexSelectOptions,
  DynamicFilterSource,
  MessageService,
} from '@wephone-utils';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import {
  FormBuilder,
  FormGroup,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { VoiceMailRepository } from '@wephone-core/model/repository/voicemail';
import { EntityManager, ConfigManager } from '@wephone-core/wephone-core.module';
import { SoundService } from '@wephone-audio';
import { IEntity } from '@wephone-core/model/model.interface';
import { FlexBasePage } from '@wephone-core-ui';
import { CallQueueEntity } from '@wephone-core/model/entity/callqueue';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { _tk, _ti } from '@wephone-translation';
import { DateAdapter } from '@angular/material/core';
import { VoicemailService, VoicemailUser, VoicemailUserFilterParams } from '@wephone-core/service/voicemail.service';
import { VoiceMailEntity } from '@wephone-core/model/entity/voicemail';
import { VMReaderData, VMReaderDataMsg } from '@wephone-core/model/repository/voicemail.i';
import { PhoneService } from '../../../services/phone/phone.service';
import { FlexDatepickerMin, PhoneNumberValidated } from '@wephone/services/form-validator';
import { FeatureName } from '@wephone-core/system';

export interface VoicemailQueueType {
  id: number;
  queue_id: number;
  calling_number: string;
  called_number: string;
  duration: number;
  is_read: number;
  is_archived: number;
  queue_name: string;
  readers?: number[];
  sound_id?: string;
  receive_dt: string;
  file_entry_ready?: boolean;
  file_entry_public_id?: string;
  action?: string;
}

// tslint:disable-next-line: no-empty-interface
export interface VoicemailUserItem extends VoicemailUser {
}

@Component({
  selector: 'app-myvoicemails',
  templateUrl: './myvoicemails.component.html',
  styleUrls: ['./myvoicemails.component.scss']
})
export class MyvoicemailsComponent extends FlexBasePage implements OnInit, OnDestroy {
  private readonly voicemailRepo = VoiceMailRepository.getInstance<VoiceMailRepository>();
  private readonly didRepo = DidRepository.getInstance<DidRepository>();
  private readonly groupRepo = UserGroupRepository.getInstance<UserGroupRepository>();

  didList = [];
  groups = [];
  unlistened_number: number;
  message_type = 'queue';

  filteredDidsMulti: ReplaySubject<DidEntity[]> = new ReplaySubject<DidEntity[]>(1);

  tableConfig: IFlexTableConfig;

  dataSource: DynamicFilterSource;
  filterForm: FormGroup;
  filtersReadonly: boolean;
  canTakeAction: boolean;
  userId: number;

  filterDidsSelectOptions: IFlexSelectOptions;
  hasFeatureGroup: boolean;
  private readonly _dataSource: BehaviorSubject<VoicemailUserItem[]>;

  constructor(
    private readonly dialogService: DialogService,
    private readonly soundService: SoundService,
    private readonly fb: FormBuilder,
    private readonly em: EntityManager,
    private readonly authService: AuthenticationService,
    private readonly configManager: ConfigManager,
    private readonly voicemailService: VoicemailService,
    private readonly messageService: MessageService,
    private readonly phoneService: PhoneService,
    cdr: ChangeDetectorRef,
    dateAdapter: DateAdapter<Date>,
  ) {
    super(cdr);
    this._dataSource = new BehaviorSubject<VoicemailUserItem[]>([]);
    this.dateAdapter = dateAdapter;

    this.filterForm = this.fb.group({
      start_dt: [DateTime.local().minus({ days: 1 }).toJSDate()],
      end_dt: [new Date()],
      groups: [[]],
      queues: [[]],
      calling_number: ['', [PhoneNumberValidated(true)]],
      numbers: [[]],
      listen_state: [''],
      archived: [false],
    }, { validator: FlexDatepickerMin('end_dt', 'start_dt') });

    this.userId = this.authService.getUserId();
    this.unlistenInfo();

    this.didRepo.loadObjectList()
      .then((dids: DidEntity[]) => {
        this.didList = dids;
        this.filteredDidsMulti.next(dids);
      })
      .catch((error: any) => {
        this.loggerService.error('Load did list error', error);
      });

    // this.loggerService.log('this.didList', this.didList);
    this.filterDidsSelectOptions = {
      compareWith: (a: DidEntity, b: DidEntity) => (a && b ? a.id === b.id : a === b),
      filterFunc: (item: DidEntity, filterString): boolean => {
        return regexSearch(item.number_with_name, filterString);
      }
    };

    this.groups = this.groupRepo.getObjectList();
    this.canTakeAction = this.authService.isAdmin() || this.authService.isSupervisor();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const ds = this.getDataSource<VoicemailUserItem>();
    this.dataSource = new DynamicFilterSource(ds, null);

    this.tableConfig = {
      rowHeight: 60,
      focusedRowHeight: 100,
      multiSelect: false,
      enableFilter: false,
      enableExportCsv: false,
      columns: [
        { name: 'voicemail', label: '' }
      ]
    };

    this.hasFeatureGroup = this.hasFeature(FeatureName.SERVICE_GROUP);
  }

  getNumbers(values: any[]): string {
    return values.map(x => x.number_with_name).join(', ');
  }

  getDataSource<T extends VoicemailUserItem = any>(): Observable<T[]> {
    return this._dataSource.asObservable() as Observable<T[]>;
  }

  get groupsControl(): AbstractControl {
    return this.filterForm.get('groups');
  }

  resolveData(): void {
    this.reloadClick();
    this.addSubscription(
      this.messageService.subscribeVMReaderUpdate((msg: VMReaderDataMsg) => {
        this.updateVoicemailReader(msg.data);
      })
    );
  }

  private updateVoicemailReader(data: VMReaderData): void {
    const item = this.dataSource.data && this.dataSource.data.find(x => x.id === data.vm_id);
    if (!item) {
      return;
    }

    if (!_.isArray(item.readers)) {
      item.readers = [data.user_id];
      item.is_read = 1;
      return;
    }

    if (!item.readers.includes(data.user_id)) {
      item.readers.push(data.user_id);
    }
  }

  isPlayerVisible(): boolean {
    return this.soundService.isPlayerVisible();
  }

  private getFilters(): VoicemailUserFilterParams {
    const formValue = this.filterForm.value;
    const startDate = parseDateTime(formValue.start_dt);
    const endDate = parseDateTime(formValue.end_dt);

    const newFilters = _.extend(_.cloneDeep(formValue), {
      start_dt: startDate && startDate.toISODate(),
      end_dt: endDate && endDate.toISODate(),
      calling_number: formValue.calling_number && formValue.calling_number.toLowerCase().trim() || '',
      called_numbers: (formValue.numbers || []).map(d => d.number)
    });

    delete newFilters.numbers;

    return newFilters;
  }

  async reloadClick(): Promise<void> {
    if (!this.filterForm.valid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    this.unlistenInfo();
    try {
      this.reloadData();
    } catch (e) {
      this.loggerService.error('Cannot reload data', e);
      this.showError(_ti('public.message.error_occurred'));
      return;
    }

    this.filtersReadonly = true;
  }

  async reloadData(): Promise<any> {
    try {
      const newFilters = this.getFilters();
      const resp: VoicemailUser[] = await this.voicemailService.getUserVoicemails(this.userId, newFilters);

      const list: VoicemailUserItem[] = [];
      for (const item of resp) {
        const vmItem: VoicemailUserItem = _.extend(item, {
        });

        list.push(vmItem);
      }

      this._dataSource.next(list);
    } catch (error) {
      this.loggerService.error('Reload data error', error);
    }
  }

  unlistenInfo(): void {
    const filters = this.getFilters();
    this.voicemailService.getUnlistenedNumber(this.userId, filters).then((total: number) => {
      this.unlistened_number = total || 0;
      this.detectChanges();
    });
  }

  bulkDelete(entities: VoicemailQueueType[]): Promise<any> {
    return this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('user.title.delete'),
      () => {
        const items: IEntity[] = [];
        for (const entity of entities) {
          const item = this.voicemailRepo.create();
          item.setObjectData(entity);
          items.push(item);
        }

        this.voicemailRepo.deleteItems(items).then(
          data => {
            // this.loggerService.log('deleteItems', data);

            for (const item of entities) {
              this.dataSource.deleteRow(item);
            }
            this.unlistenInfo();
            this.successToast(_ti('public.message.delete_success'));
          },
          resp => {
            this.loggerService.error('Delete Voicemail Error: ', resp);
          }
        );
      }
    );
  }

  deleteVoicemail(item: VoicemailQueueType): Promise<any> {
    return this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('user.title.delete'),
      () => {
        if (item) {
          this.bulkDelete([item]);
        }
      }
    );
  }

  changeFilter(): void {
    this.filtersReadonly = false;
  }

  rendererQueue(queueIds: number[]): string[] {
    const queueNames = [];
    const queueList = this.em.getRepository<CallQueueRepository>('CallQueueRepository').getObjectList() || [];
    queueList.forEach((queue: CallQueueEntity) => {
      queueIds.forEach((qId: number) => {
        if (queue.id === qId) {
          queueNames.push(queue.queue_name);
        }
      });
    });

    return queueNames;
  }

  rendererGroup(groupIds: number[]): string[] {
    const groupNames = [];
    const groupList = this.em.getRepository<UserGroupRepository>('UserGroupRepository').getObjectList() || [];
    groupList.forEach((g: UserGroupEntity) => {
      groupIds.forEach((gId: number) => {
        if (g.id === gId) {
          groupNames.push(g.name);
        }
      });
    });

    return groupNames;
  }

  rendererNumber(phoneNumbers: DidEntity[]): string[] {
    return phoneNumbers.map(x => x.display_number);
  }

  async updateListened(item: VoicemailQueueType): Promise<void> {
    if (item.is_read && _.includes(item.readers || [], this.userId)) {
      this.loggerService.error('Voice mail has been already listened');
      return;
    }

    try {
      if (!item.sound_id) {
        throw new Error('Voice message has no public id');
      }

      const ret = await this.voicemailRepo.markVoiceMailListened(item.sound_id);
      // this.loggerService.log('updateVoiceMailListened', ret);
      // if (ret && ret.item) {
      //   item.readers = ret.item.readers;
      // }
      item.is_read = 1;
      if (_.isEmpty(item.readers)) {
        item.readers = [];
      }
      if (!_.includes(item.readers, this.userId)) {
        item.readers.push(this.userId);
      }

      this.unlistenInfo();
    } catch (error) {
      this.loggerService.error('Mark voice mail listen error', error);
    }
  }

  makeCall(item: VoicemailQueueType): void {
    if (!item.called_number || !item.calling_number) {
      this.loggerService.warn('Cannot make outcall');
      return;
    }
    // this.agentService.make_outcall(item.calling_number, item.queue_id, item.called_number);
    this.phoneService.makeCall(item.calling_number, item.called_number, null);
  }

  archiveVoicemail(item: VoicemailQueueType): Promise<any> {
    if (!item) {
      this.loggerService.error('No item selected');
      return;
    }
    return this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('public.message.archive_confirm'),
      () => {
        const repo: VoiceMailRepository = this.em.getRepository<VoiceMailRepository>('VoiceMailRepository');
        const voicemail: VoiceMailEntity = repo.create() as VoiceMailEntity;
        voicemail.setObjectData(item);
        return repo.archiveItems([voicemail]).then(
          data => {
            // this.loggerService.log('archive items', data);
            this.unlistenInfo();
            this.dataSource.deleteRow(item);
            this.successToast(_ti('public.message.archive_success'));
          },
          resp => {
            this.successToast(_ti('public.message.archive_failure'));
            this.loggerService.error('Archive Voicemail Error: ', resp);
          }
        );
      }
    );
  }

  unarchiveVoicemail(item: VoicemailQueueType): Promise<any> {
    if (!item) {
      this.loggerService.error('No item selected');
      return;
    }
    return this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('public.message.unarchive_confirm'),
      () => {
        const repo: VoiceMailRepository = this.em.getRepository<VoiceMailRepository>('VoiceMailRepository');
        const voicemail: VoiceMailEntity = repo.create() as VoiceMailEntity;
        voicemail.setObjectData(item);
        return repo.unarchiveItems([voicemail]).then(
          data => {
            this.unlistenInfo();
            this.dataSource.deleteRow(item);
            this.successToast(_ti('public.message.unarchive_success'));
          },
          resp => {
            this.successToast(_ti('public.message.unarchive_failure'));
            this.loggerService.error('Unrchive Voicemail Error: ', resp);
          }
        );
      }
    );
  }

  canMakeCall(): boolean {
    return this.authService.isAdmin() || this.authService.isSupervisor() || this.authService.isAgent();
  }

  hasFeature(feature: FeatureName): boolean {
    return this.configManager.hasFeature(feature);
  }

  clearValue(event: MouseEvent, controlName: string, multiple: boolean = false, defaultValue: any = null): void {
    event.stopPropagation();
    this.filterForm.get(controlName).setValue(multiple ? [] : defaultValue);
  }
}
